import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Slider,
  Tooltip,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
// Fullscreen moved to parent page level

const AvatarPlayer = ({ room, isConnected, isStarting, sessionActive }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const audioElementsRef = useRef([]);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // Apply volume to all audio elements
  const applyVolume = useCallback((vol, muted) => {
    audioElementsRef.current.forEach((el) => {
      if (el) {
        el.volume = muted ? 0 : vol / 100;
        el.muted = muted;
      }
    });
  }, []);

  // Handle volume change
  const handleVolumeChange = (_, newValue) => {
    setVolume(newValue);
    if (newValue > 0 && isMuted) setIsMuted(false);
    applyVolume(newValue, newValue === 0);
  };

  // Toggle mute
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    applyVolume(volume, newMuted);
  };


  // Track subscription handling
  useEffect(() => {
    if (!room || !isConnected) return;

    const handleTrackSubscribed = (track, publication, participant) => {
      console.log("[AvatarPlayer] Track subscribed:", track.kind, track.sid);

      if (track.kind === "video") {
        const element = track.attach();
        if (videoRef.current) {
          videoRef.current.innerHTML = "";
          element.style.width = "100%";
          element.style.height = "100%";
          element.style.objectFit = "cover";
          element.style.borderRadius = "12px";
          videoRef.current.appendChild(element);
        }
      }

      if (track.kind === "audio") {
        // Create a new audio element
        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioEl.playsInline = true;
        audioEl.volume = isMuted ? 0 : volume / 100;
        audioEl.muted = isMuted;

        // Attach the track to the audio element
        track.attach(audioEl);

        // Add to DOM (needs to be in DOM for some browsers)
        document.body.appendChild(audioEl);

        // Store reference for volume control
        audioElementsRef.current.push(audioEl);

        // Force play
        audioEl.play().catch((err) => {
          console.warn("[AvatarPlayer] Audio autoplay blocked:", err);
        });

        console.log("[AvatarPlayer] Audio track attached, volume:", audioEl.volume);
      }
    };

    const handleTrackUnsubscribed = (track) => {
      console.log("[AvatarPlayer] Track unsubscribed:", track.kind);
      const detached = track.detach();
      detached.forEach((el) => {
        el.remove();
        audioElementsRef.current = audioElementsRef.current.filter((a) => a !== el);
      });
    };

    room.on("trackSubscribed", handleTrackSubscribed);
    room.on("trackUnsubscribed", handleTrackUnsubscribed);

    // Attach any already-subscribed tracks
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.isSubscribed && publication.track) {
          handleTrackSubscribed(publication.track, publication, participant);
        }
      });
    });

    return () => {
      room.off("trackSubscribed", handleTrackSubscribed);
      room.off("trackUnsubscribed", handleTrackUnsubscribed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, isConnected]);

  // Unlock browser autoplay policy via LiveKit
  useEffect(() => {
    if (!room || !isConnected) return;
    room.startAudio().then(() => {
      console.log("[AvatarPlayer] Room audio unlocked");
    }).catch((err) => {
      console.warn("[AvatarPlayer] Failed to unlock audio:", err);
    });
  }, [room, isConnected]);

  // Cleanup audio elements on unmount
  useEffect(() => {
    return () => {
      audioElementsRef.current.forEach((el) => {
        el.pause();
        el.srcObject = null;
        el.remove();
      });
      audioElementsRef.current = [];
    };
  }, []);

  const VolumeIcon = isMuted || volume === 0
    ? VolumeOffIcon
    : volume < 50
    ? VolumeDownIcon
    : VolumeUpIcon;

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => setShowControls(true)}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "360px",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#0a0e1a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Idle / Loading states */}
      {!isConnected && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {isStarting ? (
            <>
              <CircularProgress sx={{ color: "#7C4DFF" }} />
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Connecting to Digital Twin...
              </Typography>
            </>
          ) : !sessionActive ? (
            <Typography variant="body2" sx={{ color: "#555" }}>
              Click "Start Session" to activate the Digital Twin
            </Typography>
          ) : (
            <>
              <CircularProgress sx={{ color: "#7C4DFF" }} />
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Loading avatar stream...
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Video */}
      <Box
        ref={videoRef}
        sx={{
          width: "100%",
          height: "100%",
          display: isConnected ? "block" : "none",
        }}
      />

      {/* Controls overlay */}
      {isConnected && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
            p: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            opacity: showControls ? 1 : 0,
            transition: "opacity 0.3s ease",
            pointerEvents: showControls ? "auto" : "none",
          }}
        >
          {/* Volume controls */}
          <Tooltip title={isMuted ? "Unmute" : "Mute"}>
            <IconButton onClick={toggleMute} size="small" sx={{ color: "#fff" }}>
              <VolumeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            min={0}
            max={100}
            size="small"
            sx={{
              width: 100,
              color: "#7C4DFF",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(124,77,255,0.2)",
                },
              },
              "& .MuiSlider-track": { border: "none" },
              "& .MuiSlider-rail": { opacity: 0.3 },
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.6)", minWidth: 30 }}
          >
            {isMuted ? 0 : volume}%
          </Typography>

        </Box>
      )}
    </Box>
  );
};

export default AvatarPlayer;
